import axios from "axios";
import {
  captureException,
  captureMessage,
  withScope,
} from "@zenith/utils/sentry";

/**
 * A 401 belongs to the token wrapper, which refreshes and retries it — a session
 * expiring is expected, and reporting it here would bury the failures that are
 * not.
 */
const HANDLED_STATUS = 401;

type ApiFailure = {
  status?: number;
  message?: string;
  body?: { internalCode?: string; message?: string };
  config?: { method?: string; url?: string; headers?: Record<string, unknown> };
};

/**
 * Reports one API failure with its endpoint, status and business code. No request
 * body is attached: the app never sends a payload to Sentry.
 */
function reportApiFailure(error: unknown): void {
  // A cancelled call is how the app cleans up on unmount, not a failure.
  if (axios.isCancel(error)) return;

  const failure = (error ?? {}) as ApiFailure;
  const status = failure.status;
  if (status === HANDLED_STATUS) return;

  const method = (failure.config?.method || "GET").toUpperCase();
  const url = failure.config?.url || "(unknown url)";
  const internalCode = failure.body?.internalCode;
  const traceId = failure.config?.headers?.["trace-id"];
  // No response at all (offline, DNS, CORS, timeout), or a fault on our side.
  const serverFault = status === undefined || status >= 500;

  withScope((scope) => {
    scope.setTags({
      source: "api",
      "http.method": method,
      "http.status": status === undefined ? "network" : String(status),
      ...(internalCode ? { internalCode: String(internalCode) } : {}),
    });
    scope.setExtra("url", url);
    if (traceId) scope.setExtra("traceId", traceId);
    if (failure.body?.message) {
      scope.setExtra("apiMessage", failure.body.message);
    }

    if (serverFault) {
      captureException(failure);
    } else {
      // A 4xx is the API working as designed: worth trending, not worth waking
      // anyone for.
      captureMessage(`${method} ${url} answered ${status}`, "warning");
    }
  });
}

let installed = false;

/**
 * Reports every API failure the app makes. Installed once, before the app
 * renders, so it covers the generated clients — which all share the default
 * axios instance — without editing them.
 */
export function installApiErrorReporting(): void {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      reportApiFailure(error);
      return Promise.reject(error);
    }
  );
}
